import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Link } from 'react-router-dom'
import type { IphoneMonitoringTopic } from '@/data/iphoneMonitoringTopics'
import FeatureCrosslinks from '@/components/FeatureCrosslinks'

interface Props {
  topic: IphoneMonitoringTopic
  relatedTopics: IphoneMonitoringTopic[]
}

const IphoneMonitoringTopicTemplate: React.FC<Props> = ({ topic, relatedTopics }) => {
  return (
    <>
      <Navigation />
      <main className="min-h-screen bg-white">
        <section className="pt-40 pb-16 px-6 bg-gradient-to-b from-slate-50 via-white to-white">
          <div className="max-w-5xl mx-auto text-center space-y-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 text-slate-700 px-4 py-1 text-sm font-semibold">
              What Joey monitors today
            </span>
            <h1 className="text-4xl md:text-5xl font-semibold text-[#1C2B4A]">{topic.title}</h1>
            <p className="text-xl text-slate-600">{topic.heroSubtitle}</p>
            <p className="text-slate-500 max-w-3xl mx-auto">{topic.summary}</p>
            <div className="grid md:grid-cols-3 gap-4 text-left">
              {topic.highlights.map((item) => (
                <Card key={item} className="border-slate-200 shadow-none">
                  <CardHeader>
                    <CardTitle className="text-base text-[#1C2B4A] font-semibold">{item}</CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>
            <Button asChild className="rounded-full px-8 py-6 text-lg">
              <Link to={topic.ctaTarget}>{topic.ctaLabel}</Link>
            </Button>
          </div>
        </section>

        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-3xl font-semibold text-[#1C2B4A]">What parents learn</h2>
            <div className="space-y-4 text-slate-600 text-lg">
              {topic.overview.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 px-6 bg-slate-50">
          <div className="max-w-5xl mx-auto">
            <h3 className="text-2xl font-semibold text-[#1C2B4A] mb-6">Explore other topics</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedTopics.map((related) => (
                <Card key={related.slug} className="border-slate-200">
                  <CardContent className="p-6 space-y-3">
                    <p className="text-sm uppercase tracking-wide text-slate-400">What Joey monitors today</p>
                    <h4 className="text-xl font-semibold text-[#1C2B4A]">{related.title}</h4>
                    <p className="text-slate-600">{related.seoDescription}</p>
                    <div className="flex gap-3">
                      <Button asChild variant="outline">
                        <Link to={related.path}>View topic</Link>
                      </Button>
                      <Button asChild variant="ghost">
                        <Link to="/features/iphone-monitoring">Back to hub</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <FeatureCrosslinks current="iphone" />
      </main>
      <Footer />
    </>
  )
}

export default IphoneMonitoringTopicTemplate
